import { TherapyTypeEnum } from '@app/icare/entities/therapy/schema/TherapyType'
import { msg } from '@lingui/core/macro'
import { entityId } from '@pkg/db/schema'
import { defineEnum } from '@pkg/shared/utils/enum-builder'
import dayjs from 'dayjs'
import { z } from 'zod'
import { PatientRelation } from './PatientRelation'

export const PatientTherapyBreakTypeEnum = defineEnum({
  PAUSE: msg`Pause`,
  PAUSE_WITH_PNW: msg`Pause mit PNW`,
  HOSPITAL: msg`Krankenhaus`,
  REHAB_WITHOUT_FURTHER_CARE: msg`Reha ohne weitere Versorgung`,
  VACATION_WITHOUT_FURTHER_CARE: msg`Urlaub ohne weitere Versorgung`,
  HOSPICE_WITHOUT_FURTHER_CARE: msg`Hospiz ohne weitere Versorgung`,
  TEMPORARY_INACTIVE: msg`Vorübergehend inaktiv`,
})

export type PatientTherapyBreakType = typeof PatientTherapyBreakTypeEnum.$.Type

export const PatientTherapyId = entityId('patientTherapy')

export type PatientTherapyIdType = typeof PatientTherapyId.Type

export const PatientTherapyBreakId = entityId('patientTherapyBreak')

export type PatientTherapyBreakIdType = typeof PatientTherapyBreakId.Type

export const PatientTherapyBreakSchema = z.object({
  id: PatientTherapyBreakId.ref,
  type: z.enum(PatientTherapyBreakTypeEnum.$.zodEnum()).default(PatientTherapyBreakTypeEnum.PAUSE),
  remarks: z.string().nullish(),
  activeFrom: z.string().nullish(),
  activeTo: z.string().nullish(),
})

export type PatientTherapyBreak = z.infer<typeof PatientTherapyBreakSchema>

export const PatientTherapyBreakStatusEnum = defineEnum({
  PLANNED: msg`geplant`,
  ACTIVE: msg`aktiv`,
  ENDED: msg`beendet`,
}).$.withParsers({
  fromBreak: (brk: PatientTherapyBreak, today: dayjs.Dayjs = dayjs()) => {
    const from = brk.activeFrom ? dayjs(brk.activeFrom) : undefined
    const to = brk.activeTo ? dayjs(brk.activeTo) : undefined
    if (to && today.isAfter(to, 'day')) return 'ENDED'
    if (!from || today.isBefore(from, 'day')) return 'PLANNED'
    return 'ACTIVE'
  },
})

export type PatientTherapyBreakStatusType = typeof PatientTherapyBreakStatusEnum.$.Type

export const PatientTherapyRelationSchema = PatientRelation.extend({
  id: PatientTherapyId.schema,
  type: z.enum(TherapyTypeEnum.$.zodEnum()),
  breaks: z.array(PatientTherapyBreakSchema).default(() => []),
})
export const PatientTherapyRefSchema = z.object({
  id: PatientTherapyId.schema,
  type: z.enum(TherapyTypeEnum.$.zodEnum()),
})

export type PatientTherapyRelation = z.infer<typeof PatientTherapyRelationSchema>
